import { useCallback, useEffect } from "react";

import Axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import i18n from "i18next";
import moment from "moment";

import { getEnvironment, getFarmIdFromLocalStorage, getLanguageFromLocalStorage, getLocalKlimVar, getLocalStorageCountry } from "utils";

import { COUNTRY_CODE, ERRORS, INTERVAL_MS, LANGUAGE_CODE, MAX_REQUESTS_COUNT, TIMEOUT, TIMEOUT_INFINITE } from "consts";

import { versions } from "enums";

import { Message } from "components";

// Extend InternalAxiosRequestConfig to include custom properties
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  hideErrorMessage?: boolean;
}

// Updated to use InternalAxiosRequestConfig
interface ConfigWithMetadata extends CustomAxiosRequestConfig {
  metadata?: {
    startTime: Date;
  };
}

interface VersionItem {
  route?: string;
  method?: string;
  group: string;
  maxVersion: number;
}

const { host } = window.location;

const axios = Axios.create({
  timeout: host.includes(".local") ? TIMEOUT_INFINITE : host.includes("admin.") ? TIMEOUT_INFINITE : TIMEOUT,
});

// limits for concurrent requests
let PENDING_REQUESTS = 0;

// Updated all function signatures to use InternalAxiosRequestConfig
const authenticateRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const token = getLocalKlimVar("token");

  if (token && token !== "null" && token !== "undefined") {
    config.headers.Authorization = token;
  }

  const farmId = getFarmIdFromLocalStorage();

  if (farmId) {
    config.headers["farm-id"] = farmId;
  }

  return config;
};

const addUserLanguageToRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const language = getLanguageFromLocalStorage();
  config.headers.language = language.toLocaleLowerCase() || LANGUAGE_CODE.GERMAN;

  return config;
};

const addUserCountryToRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const country = getLocalStorageCountry();
  config.headers.country = country || COUNTRY_CODE.GERMANY;

  return config;
};

const addSessionIdToRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const sessionId = getLocalKlimVar("sessionId");

  if (sessionId) {
    config.headers["session-id"] = sessionId;
  }

  return config;
};

const addVersionHeaderToRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const token = getLocalKlimVar("token");
  config.headers.Authorization = token;

  if (!config.url) return config;

  let endpoint = config.url
    .replace(process.env.REACT_APP_API_URL || "", "")
    .replace(process.env.REACT_APP_ADMIN_API_URL || "", "")
    .split("?")[0];

  // Remove trailing slash
  endpoint = endpoint.endsWith("/") ? endpoint.slice(0, -1) : endpoint;

  const findVersion = (endpoint: string) => (item: VersionItem) => {
    let group: string;

    if (config.url?.includes("admin")) {
      group = "admin";
    } else if (config.url?.includes("general")) {
      group = "global";
    } else {
      group = "public";
    }

    // Add type guards for optional properties
    return item.route === endpoint && item.method === config.method && item.group === group && item.route !== undefined && item.method !== undefined;
  };

  let version = versions.find(findVersion(endpoint));

  if (!version) {
    // url has dynamic path segments, replace these with the wildcard character
    const pathSegments = endpoint.split("/");
    const pathToTest = pathSegments
      .map((segment) => {
        // regex check for UUID or for a Year to replace by a "*" (year match should be updated to 21 in 22nd century)
        if (segment.match(/^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i) || segment.match(/^(20)\d{2}$/i)) {
          return "*";
        }
        return segment;
      })
      .join("/");

    version = versions.find(findVersion(pathToTest));
  }

  if (!version) {
    // the dynamic path segments are not UUIDs (probably a slug for articles etc.), use old logic as fallback
    endpoint.split("/").forEach((item, i) => {
      const newEndpoint = endpoint.split("/");
      newEndpoint[i] = "*";
      const newEndpointString = newEndpoint.join("/");
      const checkVersion = versions.find(findVersion(newEndpointString));
      if (checkVersion) {
        version = checkVersion;
      }
    });
  }

  config.headers.version = version?.maxVersion || 1;

  return config;
};

const convertDataObjectToQueryString = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  // Handle nested data object
  if (config.data && typeof config.data === "object" && "data" in config.data) {
    config.data = config.data.data;
  }

  if (!config.data || config.method !== "get") {
    return config;
  }

  const queryStringParams = Object.entries(config.data)
    .filter(
      ([key, value]) =>
        key !== "signal" &&
        key !== undefined &&
        value !== undefined &&
        (!config.url?.includes(`/${value}`) ||
          key === "itemsPerPage" ||
          key === "page" ||
          value === "" ||
          value === 0 ||
          (Array.isArray(value) && value.length === 0))
    )
    .map(([key, value]) => `${key}=${value}`)
    .join("&");

  // combine original url with query string from data
  const baseUrl = config.url?.split("?0={")[0] || "";
  config.url = queryStringParams === "" ? baseUrl : `${baseUrl}?${queryStringParams}`.split("?0={")[0];

  return config;
};

const decrementRequests = (): void => {
  PENDING_REQUESTS = Math.max(0, PENDING_REQUESTS - 1);
};

/**
 *  @deprecated Do not use anymore in conjunction with react query
 * */
export const useCancelAsyncOnUnmount = (): (() => AbortSignal) => {
  const controller = new AbortController();
  const { signal } = controller;

  useEffect(
    () => () => {
      controller.abort();
      decrementRequests();
    },
    [] // eslint-disable-line
  );

  return useCallback(() => signal, []); // eslint-disable-line
};

// Updated to use InternalAxiosRequestConfig and return the correct type
const limitConcurrentRequests = (config: InternalAxiosRequestConfig): Promise<ConfigWithMetadata> =>
  new Promise((resolve) => {
    const interval = setInterval(() => {
      if (PENDING_REQUESTS < MAX_REQUESTS_COUNT) {
        PENDING_REQUESTS += 1;
        clearInterval(interval);
        resolve({
          ...config,
          metadata: { startTime: new Date() },
        });
      }
    }, INTERVAL_MS);
  });

const decrementRequestsAndReturnResponse = (response: AxiosResponse): AxiosResponse => {
  PENDING_REQUESTS = Math.max(0, PENDING_REQUESTS - 1);
  return response;
};

const returnData = (res: AxiosResponse): unknown => {
  // 204 is a success response, but we need to return no data
  if (res && res.status === 204) {
    return undefined;
  }

  if (res && res.data && typeof res.data === "object" && "data" in res.data) {
    return (res.data as { data: unknown }).data;
  }

  if (res && res.data) {
    return res.data;
  }

  return Promise.reject();
};

const responseTimer = (res: AxiosResponse): AxiosResponse => {
  const env = getEnvironment();
  if (res && env.local) {
    const config = res.config as ConfigWithMetadata;
    if (config.metadata?.startTime) {
      const duration = moment().diff(config.metadata.startTime);
      if (duration > 2000) {
        // eslint-disable-next-line
        console.error(`Endpoint ${res.config.url} took ${duration}ms to respond. This indicates a slow endpoint that MUST be improved.`);
      } else if (duration > 1250) {
        // eslint-disable-next-line
        console.warn(`Endpoint ${res.config.url} took ${duration}ms to respond. This indicates a slow endpoint that should be improved.`);
      }
    }
  }
  return res;
};

const handleHTTPError = (error: AxiosError) => {
  decrementRequests();

  // Probably a cancelled request
  if (!error || error.code === "ERR_CANCELED" || (error.response?.config as CustomAxiosRequestConfig)?.hideErrorMessage) {
    return Promise.reject(error);
  }

  if (!error.response) {
    // Loss of network connectivity is an assumption, backend could be down.
    Message({ content: i18n.t("errors.offline"), icon: "error" });
    return Promise.reject(error);
  }

  // show response code-specific error message
  // ignore silent failures (418)
  if (error.response.status !== 418) {
    const errorMessage = (ERRORS as { [key: number]: string })[error.response.status] || "errors.unknown";
    Message({ content: i18n.t(errorMessage), icon: "error" });
  }

  return Promise.reject(error);
};

axios.interceptors.request.use(limitConcurrentRequests);
axios.interceptors.request.use(authenticateRequest);
axios.interceptors.request.use(addVersionHeaderToRequest);
axios.interceptors.request.use(convertDataObjectToQueryString);
axios.interceptors.request.use(addUserLanguageToRequest);
axios.interceptors.request.use(addSessionIdToRequest);
axios.interceptors.request.use(addUserCountryToRequest);

axios.interceptors.response.use(responseTimer);
axios.interceptors.response.use(decrementRequestsAndReturnResponse);
axios.interceptors.response.use(returnData as (res: AxiosResponse) => AxiosResponse);

// this should always come last, in case an error is thrown in one of the other response interceptors
axios.interceptors.response.use((res) => res, handleHTTPError);

export { axios };
